'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FhaLoanCalculator() {
  const t = useTranslations("tools.fhaLoanCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [inputs, setInputs] = useState({
    homePrice: 500000,
    downPayment: 3.5,
    downPaymentType: 'percent',
    loanTerm: 30,
    interestRate: 6.286,
    upfrontMIP: 1.75,
    annualMIP: 0.55,
    mipDuration: 'term',
    propertyTax: 1.2,
    propertyTaxType: 'percent',
    homeInsurance: 2500,
    hoaFee: 0,
    otherCosts: 5000,
  });

  const [includeOptional, setIncludeOptional] = useState(true);
  const [results, setResults] = useState<any>(null);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const calculateMonthlyPayment = (principal: number, annualRate: number, years: number) => {
    const monthlyRate = annualRate / 100 / 12;
    const numPayments = years * 12;

    if (monthlyRate === 0) {
      return principal / numPayments;
    }

    return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
           (Math.pow(1 + monthlyRate, numPayments) - 1);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const homePrice = inputs.homePrice;
    let downPayment;
    if (inputs.downPaymentType === 'percent') {
      downPayment = homePrice * (inputs.downPayment / 100);
    } else {
      downPayment = inputs.downPayment;
    }

    const baseLoanAmount = homePrice - downPayment;
    const upfrontMIPAmount = baseLoanAmount * (inputs.upfrontMIP / 100);
    const totalLoanAmount = baseLoanAmount + upfrontMIPAmount;

    const monthlyPI = calculateMonthlyPayment(totalLoanAmount, inputs.interestRate, inputs.loanTerm);
    const totalPayments = inputs.loanTerm * 12;
    const totalPaid = monthlyPI * totalPayments;
    const totalInterest = totalPaid - totalLoanAmount;

    let mipMonths;
    if (inputs.mipDuration === 'term') {
      mipMonths = totalPayments;
    } else {
      mipMonths = Math.min(parseInt(inputs.mipDuration) * 12, totalPayments);
    }

    const monthlyMIP = (baseLoanAmount * (inputs.annualMIP / 100)) / 12;
    const totalMIP = monthlyMIP * mipMonths;

    let monthlyTax = 0, totalTax = 0;
    let monthlyInsurance = 0, totalInsurance = 0;
    let monthlyHOA = 0, totalHOA = 0;
    let monthlyOther = 0, totalOther = 0;

    if (includeOptional) {
      if (inputs.propertyTaxType === 'percent') {
        monthlyTax = (homePrice * (inputs.propertyTax / 100)) / 12;
      } else {
        monthlyTax = inputs.propertyTax / 12;
      }
      totalTax = monthlyTax * totalPayments;

      monthlyInsurance = inputs.homeInsurance / 12;
      totalInsurance = inputs.homeInsurance * inputs.loanTerm;

      monthlyHOA = inputs.hoaFee / 12;
      totalHOA = inputs.hoaFee * inputs.loanTerm;

      monthlyOther = inputs.otherCosts / 12;
      totalOther = inputs.otherCosts * inputs.loanTerm;
    }

    const totalMonthlyPayment = monthlyPI + monthlyMIP + monthlyTax + monthlyInsurance + monthlyHOA + monthlyOther;
    const totalOutOfPocket = totalPaid + totalMIP + totalTax + totalInsurance + totalHOA + totalOther;

    setResults({
      totalMonthlyPayment,
      monthlyPI,
      totalPaid,
      monthlyMIP,
      totalMIP,
      monthlyTax,
      totalTax,
      monthlyInsurance,
      totalInsurance,
      monthlyHOA,
      totalHOA,
      monthlyOther,
      totalOther,
      totalOutOfPocket,
      baseLoanAmount,
      downPayment,
      upfrontMIPAmount,
      totalInterest,
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1100px' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: '#2563eb', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #e0f2fe' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const formGroupStyle = {
    marginBottom: '1rem',
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputWrapperStyle = {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s',
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.125rem' },
    { padding: '1rem', fontSize: '1.125rem' }
  );

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const unitLabelStyle = {
    color: '#64748b',
    fontWeight: 500,
    minWidth: '50px',
  };

  const checkboxGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: '1rem 0',
  };

  const checkboxStyle = {
    width: '1.2rem',
    height: '1.2rem',
    cursor: 'pointer',
  };

  const optionalFieldsStyle = {
    background: '#f8fafc',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: includeOptional ? 'block' : 'none',
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      fontWeight: 700,
      color: '#fff',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem', borderRadius: '0.625rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem', borderRadius: '0.75rem' }
  );

  const monthlyPaymentStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const monthlyPaymentLabelStyle = getResponsiveStyle(
    { opacity: 0.9, marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' }
  );

  const monthlyPaymentAmountStyle = getResponsiveStyle(
    { fontWeight: 700 },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const breakdownHeaderStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      padding: '0.75rem',
      background: '#f1f5f9',
      borderRadius: '0.5rem',
      fontWeight: 600,
      color: '#475569',
      marginBottom: '0.5rem',
    },
    { gridTemplateColumns: '2fr 1fr', fontSize: '0.85rem' },
    { gridTemplateColumns: '2fr 1fr 1fr', fontSize: '0.9rem' },
    { gridTemplateColumns: '2fr 1fr 1fr', fontSize: '0.9rem' }
  );

  const breakdownRowStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      padding: '0.75rem',
      borderBottom: '1px solid #e2e8f0',
      alignItems: 'center',
    },
    { gridTemplateColumns: '2fr 1fr', fontSize: '0.85rem' },
    { gridTemplateColumns: '2fr 1fr 1fr', fontSize: '0.9rem' },
    { gridTemplateColumns: '2fr 1fr 1fr', fontSize: '1rem' }
  );

  const breakdownRowHighlightStyle = {
    ...breakdownRowStyle,
    background: '#dbeafe',
    borderRadius: '0.5rem',
    fontWeight: 600,
  };

  const breakdownRowTotalStyle = {
    ...breakdownRowStyle,
    background: '#fef3c7',
    borderRadius: '0.5rem',
    fontWeight: 700,
    marginTop: '0.5rem',
    borderBottom: 'none',
  };

  const breakdownValueStyle = {
    textAlign: 'right' as const,
    color: '#0f172a',
    fontWeight: 500,
  };

  const summaryCardsStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const summaryCardStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const summaryCardLabelStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.25rem',
  };

  const summaryCardValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.25rem' },
    { fontSize: '1.35rem' },
    { fontSize: '1.5rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={{ textAlign: 'center' as const, marginBottom: '2rem' }}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={glassCardStyle}>
              <form onSubmit={handleCalculate}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={h3Style}>{t("loanDetailsTitle")}</h3>

                  <div style={formGroupStyle}>
                    <label htmlFor="home-price" style={labelStyle}>{t("homePriceLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="home-price"
                        value={inputs.homePrice}
                        onChange={(e) => setInputs({...inputs, homePrice: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="1000"
                        required
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label htmlFor="down-payment" style={labelStyle}>{t("downPaymentLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="down-payment"
                        value={inputs.downPayment}
                        onChange={(e) => setInputs({...inputs, downPayment: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="0.1"
                        required
                        style={inputStyle}
                      />
                      <select
                        id="down-payment-type"
                        value={inputs.downPaymentType}
                        onChange={(e) => setInputs({...inputs, downPaymentType: e.target.value})}
                        style={selectStyle}
                      >
                        <option value="percent">%</option>
                        <option value="dollar">$</option>
                      </select>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label htmlFor="loan-term" style={labelStyle}>{t("loanTermLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="loan-term"
                        value={inputs.loanTerm}
                        onChange={(e) => setInputs({...inputs, loanTerm: parseFloat(e.target.value) || 0})}
                        min="1"
                        max="40"
                        required
                        style={inputStyle}
                      />
                      <span style={unitLabelStyle}>{t("yearsUnit")}</span>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label htmlFor="interest-rate" style={labelStyle}>{t("interestRateLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="interest-rate"
                        value={inputs.interestRate}
                        onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                        min="0"
                        max="20"
                        step="0.001"
                        required
                        style={inputStyle}
                      />
                      <span style={unitLabelStyle}>%</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={h3Style}>{t("fhaMipTitle")}</h3>

                  <div style={formGroupStyle}>
                    <label htmlFor="upfront-mip" style={labelStyle}>{t("upfrontMipLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="upfront-mip"
                        value={inputs.upfrontMIP}
                        onChange={(e) => setInputs({...inputs, upfrontMIP: parseFloat(e.target.value) || 0})}
                        min="0"
                        max="10"
                        step="0.01"
                        required
                        style={inputStyle}
                      />
                      <span style={unitLabelStyle}>%</span>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label htmlFor="annual-mip" style={labelStyle}>{t("annualMipLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="annual-mip"
                        value={inputs.annualMIP}
                        onChange={(e) => setInputs({...inputs, annualMIP: parseFloat(e.target.value) || 0})}
                        min="0"
                        max="5"
                        step="0.01"
                        required
                        style={inputStyle}
                      />
                      <span style={unitLabelStyle}>%</span>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label htmlFor="mip-duration" style={labelStyle}>{t("mipDurationLabel")}</label>
                    <select
                      id="mip-duration"
                      value={inputs.mipDuration}
                      onChange={(e) => setInputs({...inputs, mipDuration: e.target.value})}
                      style={selectStyle}
                    >
                      <option value="term">{t("loanTermOption")}</option>
                      <option value="11">{t("elevenYearsOption")}</option>
                      <option value="5">{t("fiveYearsOption")}</option>
                    </select>
                  </div>
                </div>

                <div style={checkboxGroupStyle}>
                  <input
                    type="checkbox"
                    id="include-optional"
                    checked={includeOptional}
                    onChange={(e) => setIncludeOptional(e.target.checked)}
                    style={checkboxStyle}
                  />
                  <label htmlFor="include-optional" style={{ margin: 0, cursor: 'pointer' }}>
                    <strong>{t("includeOptionalLabel")}</strong>
                  </label>
                </div>

                <div style={optionalFieldsStyle}>
                  <div style={formGroupStyle}>
                    <label htmlFor="property-tax" style={labelStyle}>{t("propertyTaxLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="property-tax"
                        value={inputs.propertyTax}
                        onChange={(e) => setInputs({...inputs, propertyTax: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="0.1"
                        style={inputStyle}
                      />
                      <select
                        id="property-tax-type"
                        value={inputs.propertyTaxType}
                        onChange={(e) => setInputs({...inputs, propertyTaxType: e.target.value})}
                        style={selectStyle}
                      >
                        <option value="percent">%</option>
                        <option value="dollar">$</option>
                      </select>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label htmlFor="home-insurance" style={labelStyle}>{t("homeInsuranceLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="home-insurance"
                        value={inputs.homeInsurance}
                        onChange={(e) => setInputs({...inputs, homeInsurance: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                        style={inputStyle}
                      />
                      <span style={unitLabelStyle}>{t("perYearUnit")}</span>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label htmlFor="hoa-fee" style={labelStyle}>{t("hoaFeeLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="hoa-fee"
                        value={inputs.hoaFee}
                        onChange={(e) => setInputs({...inputs, hoaFee: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="50"
                        style={inputStyle}
                      />
                      <span style={unitLabelStyle}>{t("perYearUnit")}</span>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label htmlFor="other-costs" style={labelStyle}>{t("otherCostsLabel")}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="other-costs"
                        value={inputs.otherCosts}
                        onChange={(e) => setInputs({...inputs, otherCosts: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                        style={inputStyle}
                      />
                      <span style={unitLabelStyle}>{t("perYearUnit")}</span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  style={calculateButtonStyle}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            {results && (
              <div style={glassCardStyle}>
                <h3 style={h2Style}>{t("resultsTitle")}</h3>

                <div style={monthlyPaymentStyle}>
                  <div style={monthlyPaymentLabelStyle}>{t("totalMonthlyPayment")}</div>
                  <div style={monthlyPaymentAmountStyle}>{formatCurrency(results.totalMonthlyPayment)}</div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div style={breakdownHeaderStyle}>
                    <div>{t("paymentComponent")}</div>
                    <div style={{ textAlign: 'right' as const }}>{t("monthly")}</div>
                    {screenSize !== 'mobile' && <div style={{ textAlign: 'right' as const }}>{t("total")}</div>}
                  </div>

                  <div style={breakdownRowHighlightStyle}>
                    <div style={{ color: '#334155' }}>{t("mortgagePaymentPI")}</div>
                    <div style={breakdownValueStyle}>{formatCurrency(results.monthlyPI)}</div>
                    {screenSize !== 'mobile' && <div style={breakdownValueStyle}>{formatCurrency(results.totalPaid)}</div>}
                  </div>

                  <div style={breakdownRowStyle}>
                    <div style={{ color: '#334155' }}>{t("annualMip")}</div>
                    <div style={breakdownValueStyle}>{formatCurrency(results.monthlyMIP)}</div>
                    {screenSize !== 'mobile' && <div style={breakdownValueStyle}>{formatCurrency(results.totalMIP)}</div>}
                  </div>

                  {includeOptional && results.monthlyTax > 0 && (
                    <div style={breakdownRowStyle}>
                      <div style={{ color: '#334155' }}>{t("propertyTax")}</div>
                      <div style={breakdownValueStyle}>{formatCurrency(results.monthlyTax)}</div>
                      {screenSize !== 'mobile' && <div style={breakdownValueStyle}>{formatCurrency(results.totalTax)}</div>}
                    </div>
                  )}

                  {includeOptional && results.monthlyInsurance > 0 && (
                    <div style={breakdownRowStyle}>
                      <div style={{ color: '#334155' }}>{t("homeInsurance")}</div>
                      <div style={breakdownValueStyle}>{formatCurrency(results.monthlyInsurance)}</div>
                      {screenSize !== 'mobile' && <div style={breakdownValueStyle}>{formatCurrency(results.totalInsurance)}</div>}
                    </div>
                  )}

                  {includeOptional && results.monthlyHOA > 0 && (
                    <div style={breakdownRowStyle}>
                      <div style={{ color: '#334155' }}>{t("hoaFee")}</div>
                      <div style={breakdownValueStyle}>{formatCurrency(results.monthlyHOA)}</div>
                      {screenSize !== 'mobile' && <div style={breakdownValueStyle}>{formatCurrency(results.totalHOA)}</div>}
                    </div>
                  )}

                  {includeOptional && results.monthlyOther > 0 && (
                    <div style={breakdownRowStyle}>
                      <div style={{ color: '#334155' }}>{t("otherCosts")}</div>
                      <div style={breakdownValueStyle}>{formatCurrency(results.monthlyOther)}</div>
                      {screenSize !== 'mobile' && <div style={breakdownValueStyle}>{formatCurrency(results.totalOther)}</div>}
                    </div>
                  )}

                  <div style={breakdownRowTotalStyle}>
                    <div style={{ color: '#334155' }}>{t("totalOutOfPocket")}</div>
                    <div style={breakdownValueStyle}>{formatCurrency(results.totalMonthlyPayment)}</div>
                    {screenSize !== 'mobile' && <div style={breakdownValueStyle}>{formatCurrency(results.totalOutOfPocket)}</div>}
                  </div>
                </div>

                <div style={summaryCardsStyle}>
                  <div style={summaryCardStyle}>
                    <div style={summaryCardLabelStyle}>{t("loanAmount")}</div>
                    <div style={summaryCardValueStyle}>{formatCurrency(results.baseLoanAmount)}</div>
                  </div>
                  <div style={summaryCardStyle}>
                    <div style={summaryCardLabelStyle}>{t("downPayment")}</div>
                    <div style={summaryCardValueStyle}>{formatCurrency(results.downPayment)}</div>
                  </div>
                  <div style={summaryCardStyle}>
                    <div style={summaryCardLabelStyle}>{t("upfrontMip")}</div>
                    <div style={summaryCardValueStyle}>{formatCurrency(results.upfrontMIPAmount)}</div>
                  </div>
                  <div style={summaryCardStyle}>
                    <div style={summaryCardLabelStyle}>{t("totalInterest")}</div>
                    <div style={summaryCardValueStyle}>{formatCurrency(results.totalInterest)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep5")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep6")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("insightsPurposeTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsPurpose1")}</p>
        <p style={paragraphStyle}>{t("insightsPurpose2")}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("insightsWhyUsefulTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsWhyUseful1")}</p>
        <p style={paragraphStyle}>{t("insightsWhyUseful2")}</p>
        <p style={paragraphStyle}>{t("insightsWhyUseful3")}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("insightsKeyRulesTitle")}</h3>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t("insightsRule1Title")}</strong> {t("insightsRule1")}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t("insightsRule2Title")}</strong> {t("insightsRule2")}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t("insightsRule3Title")}</strong> {t("insightsRule3")}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t("insightsRule4Title")}</strong> {t("insightsRule4")}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t("insightsRule5Title")}</strong> {t("insightsRule5")}</li>
        </ul>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("insightsBestPracticesTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsBestPractices1")}</p>
        <p style={paragraphStyle}>{t("insightsBestPractices2")}</p>
        <p style={paragraphStyle}>{t("insightsBestPractices3")}</p>
        <p style={paragraphStyle}>{t("insightsBestPractices4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("detailsWhatIsFHATitle")}</h3>
        <p style={paragraphStyle}>{t("detailsWhatIsFHA")}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("detailsMIPExplainedTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsMIPExplained1")}</p>
        <p style={paragraphStyle}>{t("detailsMIPExplained2")}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("detailsDownPaymentTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsDownPayment")}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("detailsLoanLimitsTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsLoanLimits")}</p>

        <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("detailsCreditRequirementsTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsCreditRequirements")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("faq1Question")}</h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("faq2Question")}</h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("faq3Question")}</h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("faq4Question")}</h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("faq5Question")}</h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h3Style, color: colors.textPrimary }}>{t("faq6Question")}</h3>
          <p style={paragraphStyle}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>{t("reference1")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("reference2")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("reference3")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("reference4")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("reference5")}</li>
        </ul>
      </section>
    </div>
  );
}